import { useAuth } from "@clerk/react";
import { Component, useEffect, useState, type ReactNode } from "react";

import { ThemeToggle } from "./components/ThemeToggle";

export const IDENTITY_LOAD_TIMEOUT_MS = 8_000;

export function IdentityBoot({ children }: { readonly children: ReactNode }) {
  const { isLoaded } = useAuth();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (isLoaded) return;
    const timer = window.setTimeout(() => {
      setSlow(true);
    }, IDENTITY_LOAD_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [isLoaded]);

  if (isLoaded) return children;
  if (slow) return <IdentityUnavailable />;
  return <IdentityOpening />;
}

export function IdentityOpening() {
  return (
    <div className="page-state page-state--boot" role="status">
      <span className="working-mark" aria-hidden="true" />
      <p>Opening this workspace…</p>
    </div>
  );
}

export function IdentityUnavailable() {
  return (
    <main className="entry-screen">
      <ThemeToggle variant="entry" />
      <div className="entry-index" aria-hidden="true">
        NL / AUTH
      </div>
      <section className="entry-card" aria-labelledby="identity-title">
        <p className="utility-label">Identity unavailable</p>
        <h1 id="identity-title">Sign-in did not load.</h1>
        <p>
          The identity service did not respond. Check the network connection,
          then try again. Closing this window hides it; it does not reload
          sign-in.
        </p>
        <div className="decision-actions">
          <button
            className="primary-action"
            type="button"
            onClick={() => {
              window.location.reload();
            }}
          >
            Try again
          </button>
        </div>
      </section>
    </main>
  );
}

export class AppErrorBoundary extends Component<
  { readonly children: ReactNode },
  { hasError: boolean; message: string }
> {
  constructor(props: { readonly children: ReactNode }) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error: Error): {
    hasError: boolean;
    message: string;
  } {
    return {
      hasError: true,
      message: error.message.replaceAll("_", " "),
    };
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <main className="entry-screen">
        <ThemeToggle variant="entry" />
        <div className="entry-index" aria-hidden="true">
          NL / AUTH
        </div>
        <section className="entry-card" aria-labelledby="shell-error-title">
          <p className="utility-label">Application stopped</p>
          <h1 id="shell-error-title">This window could not open.</h1>
          <p>
            {this.state.message || "An unexpected error stopped the viewer."}
          </p>
        </section>
      </main>
    );
  }
}
